import { type FC } from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Twitter, Instagram, Mail } from 'lucide-react';
import { useSiteSettings, type FooterLink } from '@/hooks';
import { REGISTRATIONS_CLOSED, isClosedPath } from '@/lib/registrationsClosed';

/**
 * Footer component with links and social media
 * Links are configurable through Site Settings
 */
export const Footer: FC = () => {
  const currentYear = new Date().getFullYear();
  const { settings } = useSiteSettings();

  // Filter to only show enabled links, and drop internal links to closed
  // registration routes while REGISTRATIONS_CLOSED is true. These links are
  // admin-editable content, so a "Register Now" pointed at /event-registration
  // can appear in any of the three lists; match on path, not label.
  const filterVisibleLinks = (links: FooterLink[]): FooterLink[] =>
    links.filter(link => link.enabled && !(REGISTRATIONS_CLOSED && isClosedPath(link.path)));

  const eventLinks = filterVisibleLinks(settings.footerEventLinks);
  const infoLinks = filterVisibleLinks(settings.footerInfoLinks);
  const legalLinks = filterVisibleLinks(settings.footerLegalLinks);

  // Helper to render a link (internal or external)
  const renderLink = (link: FooterLink) => {
    const isExternal = link.path.startsWith('http://') || link.path.startsWith('https://');
    const linkClasses = "text-medieval-300 hover:text-orange-500 transition-colors text-xs sm:text-sm";

    if (isExternal) {
      return (
        <a
          href={link.path}
          target="_blank"
          rel="noopener noreferrer"
          className={linkClasses}
        >
          {link.label}
        </a>
      );
    }

    return (
      <Link
        to={link.path}
        className={linkClasses}
      >
        {link.label}
      </Link>
    );
  };

  return (
    <footer className="bg-medieval-900 text-white mt-auto">
      <div className="container mx-auto px-4 py-8 sm:py-12">
        <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-6 sm:gap-6 md:gap-8">
          {/* Brand section */}
          <div className="col-span-2 space-y-3 sm:space-y-4 md:col-span-1 text-center md:text-left">
            <h3 className="text-lg sm:text-xl md:text-2xl font-viking text-primary-400">{settings.siteName}</h3>
            <p className="text-medieval-300 text-xs sm:text-sm max-w-xs mx-auto md:mx-0">
              {settings.footerTagline}
            </p>
            <div className="flex gap-4 justify-center md:justify-start">
              {settings.socialFacebook && (
                <a
                  href={settings.socialFacebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-orange-500 transition-colors"
                  aria-label="Facebook"
                >
                  <Facebook className="h-4 w-4 sm:h-5 sm:w-5" />
                </a>
              )}
              {settings.socialTwitter && (
                <a
                  href={settings.socialTwitter}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-orange-500 transition-colors"
                  aria-label="Twitter"
                >
                  <Twitter className="h-4 w-4 sm:h-5 sm:w-5" />
                </a>
              )}
              {settings.socialInstagram && (
                <a
                  href={settings.socialInstagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-orange-500 transition-colors"
                  aria-label="Instagram"
                >
                  <Instagram className="h-4 w-4 sm:h-5 sm:w-5" />
                </a>
              )}
              {settings.contactEmail && (
                <a
                  href={`mailto:${settings.contactEmail}`}
                  className="hover:text-orange-500 transition-colors"
                  aria-label="Email"
                >
                  <Mail className="h-4 w-4 sm:h-5 sm:w-5" />
                </a>
              )}
            </div>
          </div>

          {/* Event links */}
          {eventLinks.length > 0 && (
            <div>
              <h4 className="font-viking text-sm sm:text-base md:text-lg font-semibold mb-2 sm:mb-3 md:mb-4">{settings.footerEventTitle}</h4>
              <ul className="space-y-1.5 sm:space-y-2">
                {eventLinks.map((link, index) => (
                  <li key={`event-${index}-${link.label}`}>
                    {renderLink(link)}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Information links */}
          {infoLinks.length > 0 && (
            <div>
              <h4 className="font-viking text-sm sm:text-base md:text-lg font-semibold mb-2 sm:mb-3 md:mb-4">{settings.footerInfoTitle}</h4>
              <ul className="space-y-1.5 sm:space-y-2">
                {infoLinks.map((link, index) => (
                  <li key={`info-${index}-${link.label}`}>
                    {renderLink(link)}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Legal links */}
          {legalLinks.length > 0 && (
            <div>
              <h4 className="font-viking text-sm sm:text-base md:text-lg font-semibold mb-2 sm:mb-3 md:mb-4">{settings.footerLegalTitle}</h4>
              <ul className="space-y-1.5 sm:space-y-2">
                {legalLinks.map((link, index) => (
                  <li key={`legal-${index}-${link.label}`}>
                    {renderLink(link)}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Bottom bar */}
        <div className="mt-6 sm:mt-8 md:mt-12 pt-4 sm:pt-6 md:pt-8 border-t border-medieval-700 text-center text-medieval-400 text-xs sm:text-sm">
          <p>&copy; {currentYear} {settings.footerCopyright}. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
};
